import React, { useEffect } from 'react'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { FF_ENABLE_APP_ROTATION, FF_FAVUR_REDESIGN } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { isAndroidNative, isNativeNoOverride } from 'utils/platform'
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'

const AppConfigurationOverrides = ({ children }: { children?: React.ReactNode }) => {
  const appRotationFF = useFeatureFlag(FF_ENABLE_APP_ROTATION)
  const redesignFF = Boolean(useFeatureFlag(FF_FAVUR_REDESIGN))

  useEffect(() => {
    if (!isNativeNoOverride()) {
      return
    }

    if (!appRotationFF) {
      ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT)
    } else {
      ScreenOrientation.unlock()
    }
  }, [appRotationFF])

  useEffect(() => {
    if (!isNativeNoOverride()) {
      return
    }

    //workaround to set the status bar AFTER splash screen is hiden.
    // Simplify when the redesign FF is gone. We might only need to set the
    // Status bar initially here. Timeout also helps to solve it. Might be a bug
    SplashScreen.hide().then(() => {
      if (redesignFF) {
        setTimeout(() => {
          StatusBar.setStyle({ style: Style.Light })
        }, 1000)
      } else {
        StatusBar.setStyle({ style: Style.Dark })
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Workaround for android issue, when env(safe-area-inset-bottom) is 0
  // then --ion-safe-area-bottom is also 0 check if the bug is fixed
  // might have to do with chrome webview bug
  // https://forum.ionicframework.com/t/safe-area-values-always-zero-on-android-with-ionic-capacitor-version-6/243003/3
  // test and remove when you update capacitor and margins work in android native
  if (isNativeNoOverride() && isAndroidNative()) {
    SafeArea.getSafeAreaInsets().then((data) => {
      const { insets } = data
      document.body.style.setProperty('--ion-safe-area-top', `${insets.top}px`)
      //document.body.style.setProperty('--ion-safe-area-right', `${insets.right}px`)
      document.body.style.setProperty('--ion-safe-area-bottom', `${insets.bottom}px`)
      //document.body.style.setProperty('--ion-safe-area-left', `${insets.left}px`)
    })

    // setBackgroundColor and setOverlaysWebView are currently not supported on iOS devices.
    // https://capacitorjs.com/docs/apis/status-bar
    // get same edge-to-edge look for android < 15
    StatusBar.setOverlaysWebView({ overlay: true })
  }

  // once redesignFF is gone, we don't need to keep track on a useEffect
  useEffect(() => {
    if (!isNativeNoOverride()) {
      return
    }

    if (redesignFF) {
      StatusBar.setStyle({ style: Style.Light })
    } else {
      StatusBar.setStyle({ style: Style.Dark })
    }
  }, [redesignFF])

  return <>{children}</>
}

export default AppConfigurationOverrides
